import React from 'react';
import { Info } from 'lucide-react';
import { Alert, AlertTitle, AlertDescription } from '@dashboard/components/ui/alert';

const textField = (name, label) => ({
  name: `variables.labels.${name}`,
  type: 'text',
  label,
  inputClassName: 'text-sm',
  hidePlaceHolder: true,
  canReset: true,
});

const headingField = (id, label) => ({
  type: 'heading',
  id,
  label,
  level: 5,
  hideLabel: true,
});

const addressNote = {
  type: 'custom',
  renderField: () => (
    <Alert variant="default" className="my-4">
      <Info />
      <AlertTitle>Note</AlertTitle>
      <AlertDescription>
        Address field labels (Country, Address, City, State/Region, Postal code) follow each
        country&apos;s local format (e.g. ZIP vs. Postal code, State vs. County) and can&apos;t be
        customized here.
      </AlertDescription>
    </Alert>
  ),
};

const cardNote = {
  type: 'custom',
  renderField: () => (
    <Alert variant="default" className="my-4">
      <Info />
      <AlertTitle>Note</AlertTitle>
      <AlertDescription>
        Other card field labels and placeholders can&apos;t be customized. Card placeholders are
        automatically translated based on your customer&apos;s browser language.
      </AlertDescription>
    </Alert>
  ),
};

export const customLabelCategories = [
  {
    type: 'fieldgroup',
    id: 'custom-labels-product',
    collapsible: true,
    cardStyle: true,
    fields: [
      headingField('custom-labels-product', 'Product'),
      textField('intervalCountText', 'Interval count'),
      textField('dayText', 'Day'),
      textField('weekText', 'Week'),
      textField('monthText', 'Month'),
      textField('yearText', 'Year'),
      textField('trialDaysFreeText', 'Trial days free'),
      textField('trialThenText', 'Trial then'),
      textField('setupFeeText', 'Setup fee'),
      textField('startsOnText', 'Starts on'),
      textField('renewsOnText', 'Renews on'),
      textField('todayText', 'Today'),
      textField('proratedAmountText', 'Prorated amount'),
      textField('endsAfterText', 'Ends after'),
      textField('endsAfterPaymentsText', 'Ends after payments'),
      textField('variantOptionTotalText', 'Variant option total'),
      textField('payWhatYouWantText', 'Pay what you want'),
      textField('payWhatYouWantPlaceholderText', 'Pay what you want placeholder'),
      textField('pwywMinPricePlaceholderText', 'PWYW min. price placeholder'),
      textField('pwywSuggestedPricePlaceholderText', 'PWYW suggested price placeholder'),
      textField('pwywEnterAmountText', 'PWYW enter amount'),
      textField('showMoreText', 'Show more'),
      textField('showLessText', 'Show less'),
    ],
  },
  {
    type: 'fieldgroup',
    id: 'custom-labels-event',
    collapsible: true,
    cardStyle: true,
    fields: [
      headingField('custom-labels-event', 'Event'),
      // Top to bottom as they appear on the page: the event header, then each
      // ticket row (price, fee note, sale dates, remaining, status badge, line
      // total), then the canceled-event button.
      textField('virtualEventText', 'Virtual event'),
      textField('hybridEventText', 'Hybrid event'),
      textField('ticketPwywMinPricePlaceholderText', 'Ticket PWYW min. price placeholder'),
      textField(
        'ticketPwywSuggestedPricePlaceholderText',
        'Ticket PWYW suggested price placeholder',
      ),
      textField('ticketPwywEnterAmountPlaceholderText', 'Ticket PWYW enter amount placeholder'),
      textField('ticketFeeIncludesText', 'Ticket fee included'),
      textField('bookingFeeText', 'Booking fee'),
      textField('startDateText', 'Start date'),
      textField('endDateText', 'End date'),
      textField('endedAtDateText', 'Ended on'),
      textField('ticketsRemainingText', 'Tickets remaining'),
      textField('canceledText', 'Canceled'),
      textField('unavailableText', 'Unavailable'),
      textField('onSaleSoonText', 'On sale soon'),
      textField('notOnSaleText', 'Not on sale'),
      textField('ticketTypeTotalText', 'Ticket type total'),
      textField('canceledButtonText', 'Canceled button'),
    ],
  },
  {
    type: 'fieldgroup',
    id: 'custom-labels-checkout-form',
    collapsible: true,
    cardStyle: true,
    fields: [
      headingField('custom-labels-checkout-form', 'Checkout form'),
      textField('soldOutText', 'Sold out'),
      textField('soldOutButtonText', 'Sold out button'),
      textField('emailSuggestionText', 'Did you mean'),
      addressNote,
      textField('addDiscountCodeText', 'Add discount code'),
      textField('discountCodeText', 'Discount code'),
      textField('discountCodePlaceholderText', 'Discount code placeholder'),
      textField('discountCodeApplyText', 'Discount code apply'),
      textField('orPayWithCardText', 'Or pay with card'),
      textField('cardLabelText', 'Card label'),
      cardNote,
      textField('variantIsRequiredText', 'Variant is required'),
      textField('orderBumpsTitleText', 'Order bumps heading'),
      textField('orderBumpCallToActionText', 'Order bump call to action'),
      textField('orderBumpShowMoreText', 'Order bump show more'),
      textField('orderBumpShowLessText', 'Order bump show less'),
      textField('canceledText', 'Canceled'),
      textField('canceledButtonText', 'Canceled button'),
    ],
  },
  {
    type: 'fieldgroup',
    id: 'custom-labels-price-breakdown',
    collapsible: true,
    cardStyle: true,
    fields: [
      headingField('custom-labels-price-breakdown', 'Price breakdown'),
      textField('bookingFeeText', 'Booking fee'),
      textField('cartSubtotalText', 'Price breakdown subtotal'),
      textField('cartBulkDiscountText', 'Price breakdown bulk discount'),
      textField('cartCouponDiscountText', 'Price breakdown coupon discount'),
      textField('cartSetupFeeText', 'Price breakdown setup fee'),
      textField('cartTaxText', 'Price breakdown tax'),
      textField('cartTaxIncludedText', 'Price breakdown tax included'),
      textField('processingFeeContributionText', 'Price breakdown processing fee contribution'),
      textField('cartTotalOnStartDateText', 'Price breakdown total on start date'),
      textField('cartTotalAfterTrialText', 'Price breakdown total after trial'),
      textField('cartDueNowText', 'Price breakdown due now'),
      textField('cartTotalDueText', 'Price breakdown total due'),
      textField('cartTotalDueProratedAmountText', 'Price breakdown total due (prorated amount)'),
    ],
  },
  {
    type: 'fieldgroup',
    id: 'custom-labels-buttons',
    collapsible: true,
    cardStyle: true,
    fields: [
      headingField('custom-labels-buttons', 'Buttons'),
      textField('payButtonText', 'Pay button'),
      textField('subscribeButtonText', 'Subscribe button'),
      textField('submitButtonText', 'Submit button'),
      textField('getTicketsButtonText', 'Get tickets button'),
    ],
  },
  {
    type: 'fieldgroup',
    id: 'custom-labels-other',
    collapsible: true,
    cardStyle: true,
    fields: [
      headingField('custom-labels-other', 'Other'),
      textField('securePaymentsText', 'Secure payments'),
      textField('scanToOpenText', 'Scan to open'),
      textField('scanToPayText', 'Scan to pay'),
      textField('soldByText', 'Sold by'),
      textField('customerInformationText', 'Customer information'),
    ],
  },
  {
    type: 'fieldgroup',
    id: 'custom-labels-after-payment',
    collapsible: true,
    cardStyle: true,
    fields: [
      headingField('custom-labels-after-payment', 'After payment'),
      textField('upsellNoThanksText', 'Upsell no thanks'),
      textField('processingPaymentText', 'Processing payment'),
      textField('viewDownloadsText', 'View downloads'),
      textField('ticketsPurchasedText', 'Tickets purchased'),
      textField('addToCalendarText', 'Add to calendar'),
      textField('viewPaymentHistoryText', 'View payment history'),
      textField('viewSubmissionHistoryText', 'View submission history'),
    ],
  },
];

/**
 * Drops label fields the seller's flags do not unlock. Categories keep their
 * shape so callers can treat the result exactly like `customLabelCategories`.
 */
export const getCustomLabelCategories = ({ seller } = {}) =>
  customLabelCategories.map(category => ({
    ...category,
    fields: category.fields.filter(field => !field.isAvailable || field.isAvailable(seller)),
  }));
